import moment from 'moment-timezone';
import save from 'save-file';

import { LEPAnalysis } from '../components/event_viewer_modal/LEPResultsInfo';
import { getBoundsFromGoogleAutocomplete } from '../components/map/utils';
import { Config } from '../config';
import { EventStatus } from '../enums';
import {
  CreateEventInput,
  CreateEventOutput,
  Event,
  EventHistory,
  EventOutput,
  EventUpdate,
  EventsFilter,
  Flag,
  FlagInput,
  Fleet,
  GroupedEventsOutput,
  Metadata,
  ModelOutput,
  RouteFeedback,
  TagQuery,
  Tags,
} from '../interfaces';
import { setEventQueryCache } from '../queries';
import { initialEventFiltersState } from '../reducers/eventFilters/eventFiltersState';
import { deepCopy, timezonesEnum } from '../utils';
import { convertSpeedFromMpH } from '../utils/convertSpeedToMPH';
import { HttpService } from './http.service';
import { UserService } from './user.service';

const path = `${Config.API}/events`;
const pathV2 = `${Config.APIv2}/events`;

export abstract class EventsService {
  public static updateEvent = (
    id: string | number,
    input: Partial<EventUpdate>,
    localErrorHandling?: boolean,
  ): Promise<Event> => {
    const fields: Partial<EventUpdate> = deepCopy(input);

    if (fields.metadata) {
      fields.metadata = JSON.stringify(fields.metadata) as any;
    }

    const notifierEvent = new CustomEvent('event-updated', {
      detail: { id, ...fields },
    });
    window.dispatchEvent(notifierEvent);

    return HttpService.fetchAuthorized(
      `${path}/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ ...fields }),
      },
      null, // abortSignal
      localErrorHandling,
    ).then((eventFromResponse) => {
      setEventQueryCache(eventFromResponse);
      return eventFromResponse;
    });
  };

  public static getHistory = (eventId: string | number): Promise<EventHistory[]> => {
    return HttpService.fetchAuthorized(`${path}/${eventId}/activity`, {
      method: 'GET',
    }).then((res) => res);
  };

  public static getFlags = (): Promise<Flag[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/flags`, {
      method: 'GET',
    }).then((res) => res || []);
  };

  public static createFlag = (flag: FlagInput): Promise<{ id: number }> => {
    return HttpService.fetchAuthorized(`${Config.API}/flags`, {
      method: 'POST',
      body: JSON.stringify(flag),
    });
  };

  public static updateFlag = (id: number, flag: FlagInput): Promise<number> => {
    return HttpService.fetchAuthorized(`${Config.API}/flags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(flag),
    });
  };

  public static getEvent = (id: string | number): Promise<Event> => {
    return HttpService.fetchAuthorized(`${path}/${id}`).then((res: Event) => {
      sortSubEvents(res);
      inheritMetadata(res);
      return res;
    });
  };

  public static createEvent = (event: CreateEventInput): Promise<CreateEventOutput> => {
    return HttpService.fetchAuthorized(`${Config.API}/events`, {
      method: 'POST',
      body: JSON.stringify(event),
    }).then((res) => res);
  };

  public static getEvents = (filter: EventsFilter, fleet: Fleet, abortSignal?: AbortSignal): Promise<EventOutput> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone ?? timezonesEnum.AMERICA_NEW_YORK);
    return HttpService.fetchAuthorized(
      `${pathV2}/query`,
      {
        method: 'POST',
        body: JSON.stringify({ ...filters }),
      },
      abortSignal,
    ).then((res: EventOutput) => {
      if (res === null || !Array.isArray(res.events)) {
        return null;
      }

      res?.events?.forEach((e) => {
        sortSubEvents(e);
        inheritMetadata(e);

        setEventQueryCache(e);

        e?.sub_events?.forEach((subevent) => {
          setEventQueryCache(subevent);
        });
      });

      return res.events ? res : { cursor: null, events: [] };
    });
  };

  public static getGroupedEvents = (
    filter: EventsFilter,
    fleet: Fleet,
    abortSignal?: AbortSignal,
  ): Promise<EventOutput> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone ?? timezonesEnum.AMERICA_NEW_YORK);
    return HttpService.fetchAuthorized(
      `${pathV2}/groups/query`,
      {
        method: 'POST',
        body: JSON.stringify({ ...filters }),
      },
      abortSignal,
    ).then((res: GroupedEventsOutput) => {
      if (res === null || !Array.isArray(res.items)) {
        return null;
      }

      // convert data format to match "matching" data format
      const matchedEvents: EventOutput = {
        cursor: res.cursor ?? null,
        events: [],
      };

      res?.items?.forEach((group) => {
        const matchedEvent = deepCopy(group.events[0]);
        matchedEvent.sub_events = deepCopy(group.events);

        group.events.forEach(setEventQueryCache);
        matchedEvents.events.push(matchedEvent);
      });

      return matchedEvents;
    });
  };

  public static skipEvents = (event_ids: number[]): Promise<void> => {
    // If no events to skip, just resolve the promise
    if (event_ids.length === 0) {
      return Promise.resolve();
    }

    return HttpService.fetchAuthorized(`${pathV2}/groups/skip`, {
      method: 'POST',
      body: JSON.stringify({ event_ids }),
    });
  };

  public static makeGroupedEvents = (
    filter: EventsFilter,
    fleet: Fleet,
    abortSignal?: AbortSignal,
  ): Promise<EventOutput> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone ?? timezonesEnum.AMERICA_NEW_YORK);
    return HttpService.fetchAuthorized(
      `${pathV2}/groups/make?flush=false`,
      {
        method: 'POST',
        body: JSON.stringify({ ...filters }),
      },
      abortSignal,
    ).then((res: GroupedEventsOutput) => {
      if (res === null || !Array.isArray(res.items)) {
        return null;
      }

      // convert data format to match "matching" data format
      const matchedEvents: EventOutput = {
        ...res,
        cursor: res.cursor ?? null,
        events: [],
      };

      res?.items?.forEach((group) => {
        const matchedEvent = deepCopy(group.events[0]);
        matchedEvent.sub_events = deepCopy(group.events);

        group.events.forEach(setEventQueryCache);
        matchedEvents.events.push(matchedEvent);
      });

      return matchedEvents;
    });
  };

  public static listGroupedEvents = (abortSignal?: AbortSignal, includeSkipped?: boolean): Promise<EventOutput> => {
    return HttpService.fetchAuthorized(
      `${pathV2}/groups/list?skipped=${Boolean(includeSkipped).toString()}`,
      {
        method: 'GET',
      },
      abortSignal,
    ).then((res: GroupedEventsOutput) => {
      if (res === null || !Array.isArray(res.items)) {
        return null;
      }

      // convert data format to match "matching" data format
      const matchedEvents: EventOutput = {
        ...res,
        events: [],
      };

      res?.items?.forEach((group) => {
        const matchedEvent = deepCopy(group.events[0]);
        matchedEvent.sub_events = deepCopy(group.events);

        group.events.forEach(setEventQueryCache);
        matchedEvents.events.push(matchedEvent);
      });

      return matchedEvents;
    });
  };

  public static extendAssignmentTime = () => {
    return HttpService.fetchAuthorized(`${pathV2}/groups/reset`, {
      method: 'POST',
    });
  };

  public static flushAssignedEvents = () => {
    return HttpService.fetchAuthorized(`${pathV2}/groups/flush`, {
      method: 'POST',
    });
  };

  public static getEventMetadata = (id: number) => {
    return HttpService.fetchAuthorized(
      `${path}/${id}/metadata`,
      {
        method: 'GET',
      },
      null,
      true,
    ).then((res: Metadata) => res);
  };

  public static getMapEvents = (
    filter: EventsFilter,
    fleet: Fleet,
    abortSignal?: AbortSignal,
  ): Promise<EventOutput> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone ?? timezonesEnum.AMERICA_NEW_YORK);
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/map_events`,
      {
        method: 'POST',
        body: JSON.stringify({ ...filters }),
      },
      abortSignal,
    ).then((res) => {
      if (res === null || !Array.isArray(res.events)) {
        return null;
      }

      return res.events ? res : { cursor: null, events: [] };
    });
  };

  public static getCount = (filter: EventsFilter, fleet: Fleet, abortSignal?: AbortSignal): Promise<number | -1> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone ?? timezonesEnum.AMERICA_NEW_YORK);

    return HttpService.fetchAuthorized(
      `${Config.APIv2}/events_count`,
      {
        method: 'POST',
        body: JSON.stringify({ ...filters }),
      },
      abortSignal,
    ).then((res) => res ?? -1);
  };

  public static generateReport = (filter: EventsFilter, fleet: Fleet): Promise<{ url: string; headUrl: string }> => {
    const queryString = EventsService.getQueryString(filter, fleet.timezone ?? timezonesEnum.AMERICA_NEW_YORK);

    return HttpService.fetchAuthorized(`${Config.API}/reports/csv/eventsdetails?${queryString}`, {
      method: 'POST',
    }).then((res) => res);
  };

  public static sanitizeFilters = (filter: EventsFilter, fleetTimeZone: string, removeMediaStatus = false) => {
    // TODO -- state.eventTags should be named state.eventTypes
    const { selectedFilters } = filter;

    const isInternal = UserService.isInternalSync();

    // TODO https://github.com/HaydenAI-Org/SafeSenseWebApp/issues/45
    let left_bottom_lon = -180;
    let left_bottom_lat = -90;
    let right_top_lon = 180;
    let right_top_lat = 90;

    const bounds = filter.bounds || getBoundsFromGoogleAutocomplete(filter.location);
    if (bounds) {
      right_top_lon = bounds.ne.lng;
      right_top_lat = bounds.ne.lat;
      left_bottom_lon = bounds.sw.lng;
      left_bottom_lat = bounds.sw.lat;
    }

    if (filter.zero_coordinates_filter) {
      right_top_lon = 0;
      right_top_lat = 0;
      left_bottom_lon = 0;
      left_bottom_lat = 0;
    }

    const obj: any = {
      limit: 25,
      order: 'DESC', // newest first by default
      ...initialEventFiltersState.filter,
      ...filter,
      bbq: {
        left_bottom_lon,
        left_bottom_lat,
        right_top_lon,
        right_top_lat,
      },
      lrq: {
        location_lat: filter.location_lat,
        location_lon: filter.location_lon,
        location_radius_miles: filter.location_radius_miles,
      },
      selected_filters: selectedFilters || [],
      // pass as enum name
      flag: filter.flag ? `${filter.flag}` : undefined,
      tag_query: filter.tags && Object.entries(filter.tags).length > 0 ? buildTagQueryObject(filter.tags) : undefined,
    };

    delete obj.selectedFilters; // no need for duplication
    delete obj.location; // for internal use only right now
    delete obj.bounds; // for internal use only right now
    if (removeMediaStatus) {
      delete obj.media_statuses;
    }

    if (!isInternal) {
      delete obj.human_review;
    }

    if (filter.status !== undefined) {
      obj.status = filter.status;
    } else {
      delete obj.status;
    }

    if (filter.order !== undefined) {
      obj.order = filter.order;
    }

    if (obj.license_plates) {
      obj.license_plates = obj.license_plates
        .map((v: string) => v?.toUpperCase())
        .filter(Boolean)
        .map((v: string) => v.replace(new RegExp(/\*/, 'g'), '%'));
    }

    if (obj.block_names) {
      obj.block_names = obj.block_names
        .filter(Boolean)
        .map((v: string) => v.trim().replace(new RegExp(/\*/, 'g'), '%'));
    }

    if (obj.bus_stop_ids) {
      obj.bus_stop_ids = obj.bus_stop_ids
        .filter(Boolean)
        .map((v: string) => v.trim().replace(new RegExp(/\*/, 'g'), '%'));
    }

    if (obj.vehicle_speed_lte) {
      obj.vehicle_speed_lte = convertSpeedFromMpH(obj.vehicle_speed_lte);
    }
    if (obj.vehicle_speed_gte) {
      obj.vehicle_speed_gte = convertSpeedFromMpH(obj.vehicle_speed_gte);
    }

    Object.keys(obj).forEach((key) => {
      if (obj[key] === undefined || obj[key] === null || (Array.isArray(obj[key]) && !obj[key].length)) {
        delete obj[key];
      }

      // convert numeric strings to numbers
      if (!['flag'].includes(key)) {
        if (typeof obj[key] === 'string' && !Number.isNaN(Number(obj[key]))) {
          obj[key] = Number(obj[key]);
        }
      }
    });

    delete obj.tags;
    obj.include_tags = true;

    Object.keys(obj).forEach((key) => {
      if (!['auto_exposure', 'include_tags'].includes(key)) {
        if (typeof obj[key] === 'boolean') {
          obj[key] = obj[key] ? 'true' : 'false';
        }
      }
    });

    return obj;
  };

  public static getQueryString = (filter: EventsFilter, fleetTimeZone: string) => {
    const obj = EventsService.sanitizeFilters(filter, fleetTimeZone);
    let res = new URLSearchParams(obj as any).toString();

    if (filter.tags && Object.keys(filter.tags).length > 0) {
      const tag_query = buildTagQuery(filter.tags);
      res += `&tag_query=${encodeURIComponent(tag_query)}`;
    }

    return res;
  };

  public static getLEPAnalysis = (eventId: number): Promise<LEPAnalysis> => {
    return HttpService.fetchAuthorized(
      `${Config.API}/events/${eventId}/lep_analysis`,
      {
        method: 'GET',
      },
      null,
      true,
    );
  };

  public static getModelOutputs = (eventId: number): Promise<ModelOutput[]> => {
    return HttpService.fetchAuthorized(
      `${Config.API}/events/${eventId}/model_outputs`,
      {
        method: 'GET',
      },
      null,
      true,
    ).then((res) => res?.outputs);
  };

  public static getRouteFeedback = (eventId: number): Promise<RouteFeedback> => {
    return HttpService.fetchAuthorized(
      `${Config.API}/events/${eventId}/route_feedback`,
      {
        method: 'GET',
      },
      null,
      true,
    );
  };

  public static getIAT = (eventId: number) => {
    return HttpService.fetchAuthorized(
      `${Config.API}/events/${eventId}/iat`,
      {
        method: 'GET',
      },
      null,
      true,
    ).then((res) => {
      save(res, `iat-report-${eventId}.pdf`);
    });
  };

  public static getDeviceIAT = (deviceId: string, fleet: Fleet) => {
    const filter: EventsFilter = {
      limit: 1,
      device_ids: [deviceId],
      status: EventStatus.approved,
      dateFrom: moment().subtract(180, 'days').startOf('day').toISOString(),
      dateTo: moment().endOf('day').toISOString(),
    };
    return EventsService.getEvents(filter, fleet).then((res) => {
      if (res.events.length > 0) {
        return HttpService.fetchAuthorized(
          `${Config.API}/events/${res.events[0].id}/iat`,
          {
            method: 'GET',
          },
          null,
          true,
        ).then((res) => {
          save(res, `iat-report-${deviceId}.pdf`);
        });
      }
    });
  };

  public static requestNewEvidence = (eventId: number): Promise<{ package_name: string }> => {
    const url = `${Config.API}/events/${eventId}/evidences`;

    return HttpService.fetchAuthorized(url, { method: 'POST' }).then((res) => res);
  };

  public static setEventMatches = (eventId: number, subeventIds: number[]) => {
    const body = {
      sub_events: [...subeventIds],
    };
    return HttpService.fetchAuthorized(
      `${Config.API}/events/${eventId}/match`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      null,
      true,
    );
  };
}

const sortSubEvents = (event: Event) => {
  if (!event) {
    return;
  }

  if (event.sub_events) {
    event.sub_events.sort((se1, se2) => moment(se1.captured_at).valueOf() - moment(se2.captured_at).valueOf());
  }
};

const inheritMetadata = (event: Event) => {
  if (!event) {
    return;
  }

  if (event.sub_events) {
    event.sub_events.forEach((se) => {
      se.metadata = { ...event.metadata, ...se.metadata };
    });
  }
};

const buildTagQueryObject = (tags: Tags): any => {
  // If we have multiple tags selected, we use AND logic to filter events.
  // Backend also supports other operators, but the FE for that would have to be
  // pretty crazy. Lets leave these advanced use cases for th DB diggers
  let tag_query: TagQuery = {
    node_type: 'and',
    kids: Object.entries(tags).map(([key, value]) => {
      return {
        node_type: 'leaf',
        kids: null,
        tag_predicate: {
          tag_definition_id: parseInt(key.split(':')[1], 10),
          op: '=',
          value,
        },
      };
    }),
  };

  // However, if we only have 1 entry, we can't use AND logic so we use the
  // single tag as the end leaf
  if (Object.keys(tags).length === 1) {
    const key = Object.keys(tags)?.[0];
    tag_query = {
      node_type: 'leaf',
      kids: null,
      tag_predicate: {
        tag_definition_id: parseInt(key.split(':')[1], 10),
        op: '=',
        value: tags[key],
      },
    };
  }

  return tag_query;
};

const buildTagQuery = (tags: Tags): string => {
  const tag_query = buildTagQueryObject(tags);
  let stringifiedQuery = JSON.stringify(tag_query);

  // remove quotes around number, cause backend doesn't like them
  const regex = /"(-|)([0-9]+(?:\.[0-9]+)?)"/g;
  stringifiedQuery = stringifiedQuery.replace(regex, '$1$2');
  stringifiedQuery = stringifiedQuery.replaceAll('%22null%22', 'null');

  return stringifiedQuery;
};
